import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Copy, FileText, Plus, UserX } from 'lucide-react';
import type { PrescriptionStatus, PrescriptionDocType } from '@dpfs/shared';
import { patientsService } from '@/services/patients';
import { getCountryName } from '@/data/countries';
import { PageHeader, StatusBadge, EmptyState, LoadingState } from '@/components/ui';

// Shape returned by GET /patients/:id/prescriptions (see api patients.service getPatientPrescriptions).
interface PatientPrescriptionItem {
  id: string;
  drug?: { id: string; tradeName: string } | null;
  itemLabel?: string | null;
  dosage?: string | null;
  frequency?: string | null;
  duration?: string | null;
  quantity?: number | null;
  quantityUnit?: string | null;
  instructions?: string | null;
  substitutionAllowed?: boolean;
}

interface PatientPrescription {
  id: string;
  status: PrescriptionStatus | string;
  documentType?: PrescriptionDocType | null;
  createdAt: string;
  expiresAt: string;
  diagnosis?: string | null;
  items?: PatientPrescriptionItem[];
  fulfillments?: { id: string }[];
}

function InfoItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="mt-0.5 break-words font-medium text-gray-900">{children}</dd>
    </div>
  );
}

export function PatientDetail() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: patient, isLoading: patientLoading } = useQuery({
    queryKey: ['patient', id],
    queryFn: () => patientsService.getById(id!),
    enabled: !!id,
  });

  const { data: prescriptionsData, isLoading: prescriptionsLoading, isError: prescriptionsError } = useQuery({
    queryKey: ['patient', id, 'prescriptions'],
    queryFn: () => patientsService.getPrescriptions(id!),
    enabled: !!id,
  });

  if (patientLoading) {
    return <LoadingState />;
  }

  if (!patient) {
    return (
      <div>
        <PageHeader title={t('patient.title')} backTo="/doctor/patients" backLabel={t('doctor.patients.backToList')} />
        <div className="card p-0">
          <EmptyState icon={UserX} title={t('doctor.patients.notFound')} />
        </div>
      </div>
    );
  }

  const prescriptions = (prescriptionsData?.prescriptions ?? []) as PatientPrescription[];
  const newRxPath = `/doctor/prescriptions/new?patientId=${id}`;

  const duplicate = (prescription: PatientPrescription) => {
    const itemsParam = encodeURIComponent(
      JSON.stringify(
        prescription.items?.map((item) => ({
          drugId: item.drug?.id,
          quantity: item.quantity,
          quantityUnit: item.quantityUnit,
          dosage: item.dosage,
          frequency: item.frequency,
          duration: item.duration,
          instructions: item.instructions,
          substitutionAllowed: item.substitutionAllowed,
        })) || []
      )
    );
    navigate(`/doctor/prescriptions/new?patientId=${id}&duplicate=${prescription.id}&items=${itemsParam}`);
  };

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        backTo="/doctor/patients"
        backLabel={t('doctor.patients.backToList')}
        title={
          <span className="break-words">
            {patient.lastName} {patient.firstName}
          </span>
        }
        description={
          patient.lastNameAr ? (
            <span dir="rtl" className="text-base">
              {patient.lastNameAr} {patient.firstNameAr}
            </span>
          ) : undefined
        }
        actions={
          <Link to={newRxPath} className="btn-primary">
            <Plus className="h-4 w-4" />
            {t('prescription.new')}
          </Link>
        }
      />

      {/* Patient info */}
      <div className="card mb-6">
        <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <InfoItem label={patient.identityType === 'PASSPORT' ? t('patient.passport') : t('patient.nin')}>
            <span className="font-mono">
              {(patient.identityType === 'PASSPORT' ? patient.passport : patient.nin) || '—'}
            </span>
          </InfoItem>
          {patient.identityType === 'PASSPORT' && (
            <InfoItem label={t('patient.country')}>{getCountryName(patient.country)}</InfoItem>
          )}
          <InfoItem label={t('patient.dateOfBirth')}>
            {patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString(dateLocale) : '—'}
          </InfoItem>
          <InfoItem label={t('patient.gender')}>{t(`patient.${patient.gender.toLowerCase()}`)}</InfoItem>
          {patient.phone && (
            <InfoItem label={t('patient.phone')}>
              <span dir="ltr">{patient.phone}</span>
            </InfoItem>
          )}
        </dl>

        {patient.address && (
          <dl className="mt-4 border-t border-gray-100 pt-4">
            <InfoItem label={t('patient.address')}>{patient.address}</InfoItem>
          </dl>
        )}
      </div>

      {/* Prescription History */}
      <div className="card p-0">
        <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <h2 className="card-title">
            {t('doctor.patients.history')}
            {!prescriptionsLoading && (
              <span className="ms-2 text-sm font-normal text-gray-500">({prescriptions.length})</span>
            )}
          </h2>
        </div>

        {prescriptionsLoading ? (
          <LoadingState className="py-10" />
        ) : prescriptionsError ? (
          <div className="p-5">
            <div className="alert-danger">{t('doctor.loadError')}</div>
          </div>
        ) : prescriptions.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={t('doctor.patients.noPrescriptions')}
            action={
              <Link to={newRxPath} className="btn-primary btn-sm">
                <Plus className="h-4 w-4" />
                {t('doctor.patients.createPrescription')}
              </Link>
            }
          />
        ) : (
          <ul className="divide-y divide-gray-100">
            {prescriptions.map((prescription) => {
              const isOrderDoc = !!prescription.documentType && prescription.documentType !== 'MEDICATION';
              const fulfillmentCount = prescription.fulfillments?.length ?? 0;
              return (
                <li
                  key={prescription.id}
                  className="flex flex-col gap-3 px-5 py-4 transition-colors hover:bg-gray-50 sm:flex-row sm:items-start"
                >
                  <Link to={`/doctor/prescriptions/${prescription.id}`} className="block min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <StatusBadge status={prescription.status} />
                      <span className="text-sm tabular-nums text-gray-500">
                        {new Date(prescription.createdAt).toLocaleDateString(dateLocale)}
                      </span>
                    </div>
                    <p className="font-medium text-gray-900">
                      {isOrderDoc
                        ? t(`prescription.docTypes.${prescription.documentType!.toLowerCase()}`)
                        : t('doctor.patients.medicationsCount', { n: prescription.items?.length || 0 })}
                    </p>
                    {prescription.items && prescription.items.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {prescription.items.map((item, idx) => (
                          <li key={item.id ?? idx} className="break-words text-sm">
                            <span className="font-medium text-gray-800">{item.drug?.tradeName || item.itemLabel}</span>
                            {(item.dosage || item.frequency || item.duration) && (
                              <span className="text-gray-500">
                                {' — '}
                                {[item.dosage, item.frequency, item.duration].filter(Boolean).join(', ')}
                              </span>
                            )}
                            {item.quantity != null && (
                              <span className="text-gray-400"> ({item.quantity} {item.quantityUnit || 'x'})</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {prescription.diagnosis && (
                      <p className="mt-1 break-words text-sm text-gray-500">
                        {t('prescription.diagnosis')}: {prescription.diagnosis}
                      </p>
                    )}
                  </Link>
                  <div className="flex shrink-0 items-center justify-between gap-3 sm:flex-col sm:items-end">
                    <div className="text-sm text-gray-500">
                      {fulfillmentCount > 0 ? (
                        <span className="text-success-700">
                          {t('doctor.patients.fulfillmentsCount', { n: fulfillmentCount })}
                        </span>
                      ) : prescription.status === 'ACTIVE' ? (
                        <span>
                          {t('doctor.expiresOn', {
                            date: new Date(prescription.expiresAt).toLocaleDateString(dateLocale),
                          })}
                        </span>
                      ) : null}
                    </div>
                    {!isOrderDoc && (
                      <button
                        type="button"
                        onClick={() => duplicate(prescription)}
                        className="btn-ghost btn-sm"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        {t('doctor.duplicate')}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
